import { camelCase } from 'lodash';
import { v4 } from 'uuid';
import { ACTIVITIES_DEFAULT_DATA } from '@shared/constants/activities.constants';
import { ActivityId } from '@shared/models/activity-class.model';
import {
  Activity,
  ActivityEnum,
  IProjectActivities,
} from '@shared/models/activities.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { toEnergyActivity } from '@shared/helpers/energy/to-energy-activity.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { isNumeric, toNumber } from '@shared/helpers/math.helpers';

const INDUSTRY_ACTIVITY_IDS = new Set<ActivityId>([
  ActivityId.IndustryOffice,
  ActivityId.Industry,
  ActivityId.Warehouse,
  ActivityId.Brewery,
  ActivityId.ConcreteIndustry,
]);

/**
 * @deprecated Legacy-only mirror of {@link getActivityTypeName} in activities.helpers.
 */
const getLegacyActivityTypeName = (type: ActivityEnum): string => {
  switch (type) {
    case ActivityEnum.Apartments:
      return 'Apartments';
    case ActivityEnum.Stairwells:
      return 'Stairwells';
    case ActivityEnum.LaundryRoom:
      return 'Laundry Room';
    case ActivityEnum.BikeRoom:
      return 'Bike Room';
    case ActivityEnum.Storage:
      return 'Storage';
    case ActivityEnum.BuildingUtilities:
      return 'Building Utilities';
    case ActivityEnum.Garage:
      return 'Garage';
    case ActivityEnum.Other:
    default:
      return 'Other';
  }
};

/**
 * Create a legacy metadata {@link Activity} without importing activities.helpers (avoids import cycles).
 * @param activityType Activity enum type.
 * @returns New legacy activity instance.
 */
const createLegacyMetadataActivity = (activityType: ActivityEnum): Activity => {
  const defaultKey = camelCase(
    getLegacyActivityTypeName(activityType),
  ) as keyof IProjectActivities;

  return {
    id: v4(),
    ...ACTIVITIES_DEFAULT_DATA[defaultKey],
    type: activityType,
    name: '',
    activityId: ActivityId.Dummy,
  };
};

/**
 * Detect legacy industry activity ids.
 * @param activityId Legacy activity id.
 * @returns True when activity is industry-related.
 */
const isIndustryActivityId = (
  activityId: ActivityId | undefined,
): activityId is ActivityId =>
  activityId !== undefined && INDUSTRY_ACTIVITY_IDS.has(activityId);

/**
 * @deprecated Older version of the activity model stored gfa as string in the database.
 * @param activity Legacy activity row.
 * @returns Activity with numeric gfa when parseable.
 */
export const migrateStringGfa = (activity: Activity): Activity => ({
  ...activity,
  gfa:
    'gfa' in activity && isNumeric(activity.gfa)
      ? toNumber(activity.gfa)
      : undefined,
});

/**
 * @deprecated Only use this for migrations
 * Create an activity from an activity id.
 * @param activity_id Legacy activity id.
 * @returns Legacy metadata activity.
 */
export const createActivityFromActivityId = (
  activity_id?: ActivityId,
): Activity => {
  if (isIndustryActivityId(activity_id)) {
    return {
      ...createLegacyMetadataActivity(ActivityEnum.Other),
      activityId: activity_id,
    };
  }
  const energyActivity = toEnergyActivity(activity_id);
  switch (energyActivity) {
    case ActivityType.Office:
      return {
        ...createLegacyMetadataActivity(ActivityEnum.Other),
        activityId: ActivityId.Office,
      };
    case ActivityType.School:
      return {
        ...createLegacyMetadataActivity(ActivityEnum.Other),
        activityId: ActivityId.School,
      };
    case ActivityType.Residential:
      return {
        ...createLegacyMetadataActivity(ActivityEnum.Apartments),
        activityId: ActivityId.Residential,
      };
    case ActivityType.Garage:
      return {
        ...createLegacyMetadataActivity(ActivityEnum.Garage),
        activityId: ActivityId.EmployeeParking,
      };
    case ActivityType.Other:
      return createLegacyMetadataActivity(ActivityEnum.Other);
    default:
      return assertUnreachable(energyActivity);
  }
};
